<template>
  <ul>
    <li v-if="showDebug">
      <div v-for="(item, key) in viewColumn" :key="key">
        <span style="font-weight: 500">{{ key }}:</span> {{ item }}
      </div>
    </li>
    <!--SORT UP-->
    <li @click="tableStore.setSorting({ columnName: viewColumn.field, order: 'ASC' })">
      <font-awesome-icon :icon="['fal', 'arrow-down-a-z']" class="menuitem-icon" />
      <span>Sorter stigende</span>
    </li>
    <!--SORT DOWN-->
    <li @click="tableStore.setSorting({ columnName: viewColumn.field, order: 'DESC' })">
      <font-awesome-icon :icon="['fal', 'arrow-down-z-a']" class="menuitem-icon" />
      <span>Sorter synkende</span>
    </li>
    <li class="separator" />
    <!--INPUT-->
    <li>
      <div class="checkbox-container">
        <BaseCheckbox
          v-model="selectAll"
          label="Velg alle"
          @update:modelValue="toggleAll"
        />
        <div class="checkbox-list">
          <BaseCheckbox
            v-for="option in formattedOptions"
            :key="option.value"
            :label="option.label"
            :model-value="selectedValues[option.value]"
            @update:modelValue="(value) => handleCheckboxChange(option.value, value)"
          />
        </div>
      </div>
    </li>
    <!--REMOVE FILTERS-->
    <li @click="tableStore.clearFilter(viewColumn.field)">
      <font-awesome-icon :icon="['fal', 'filter-slash']" class="menuitem-icon remove" />
      <span>Fjern alle filter</span>
    </li>
    <li class="separator" />
    <!--FILTER NULL-->
    <li @click="tableStore.addFilterNull(viewColumn.field)">
      <span style="padding-left: 28px">Filter <strong>p&aring;</strong> blanke</span>
    </li>
    <!--FILTER NOT NULL-->
    <li @click="tableStore.addFilterNotNull(viewColumn.field)">
      <span style="padding-left: 28px">Filter <strong>bort</strong> blanke</span>
    </li>
    <li class="separator" />
    <!--REFRESH-->
    <li @click="() => { tableStore.refreshQuery(); $emit('close') }">
      <font-awesome-icon :icon="['fal', 'play']" class="menuitem-icon play" />
      <span>Oppdater</span>
    </li>
  </ul>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useGlobalStore } from '@/store/storeGlobal'
import BaseCheckbox from '@/components/BaseComponents/BaseCheckbox.vue'

// GlobalStore
const globalStore = useGlobalStore()
const { showDebug } = storeToRefs(globalStore)

const props = defineProps({
  viewColumn: { type: Object, required: true }
})

const tableStore = useTableStore()
const { whereItems } = storeToRefs(tableStore)

const selectAll = ref(false)
const selectedValues = ref({})

const formattedOptions = computed(() => {
  return props.viewColumn.simpleList || []
})

// Oppdater watchEffect for å initialisere selectedValues basert på eksisterende filtre
watchEffect(() => {
  const existingFilter = whereItems.value.find(item =>
    item.columnName === props.viewColumn.field && item.operator === 'in'
  )

  formattedOptions.value?.forEach(option => {
    selectedValues.value[option.value] = existingFilter
      ? existingFilter.value.includes(option.value)
      : false
  })

  selectAll.value = formattedOptions.value?.length > 0 &&
    formattedOptions.value.every(option => selectedValues.value[option.value])
})

function handleCheckboxChange (value, checked) {
  selectedValues.value[value] = checked
  updateFilters()

  // Oppdater selectAll status
  selectAll.value = formattedOptions.value?.every(option => selectedValues.value[option.value])
}

function toggleAll (value) {
  selectAll.value = value
  formattedOptions.value?.forEach(option => {
    selectedValues.value[option.value] = value
  })
  updateFilters()
}

function updateFilters () {
  const selectedItems = Object.entries(selectedValues.value)
    .filter(([, isSelected]) => isSelected)
    .map(([value]) => value)

  if (selectedItems.length > 0) {
    tableStore.addFilterActionOperator(
      props.viewColumn.field,
      'in',
      selectedItems  // Store vil håndtere stringifying før det sendes til backend
    )
  } else {
    tableStore.clearFilter(props.viewColumn.field)
  }
}
</script>

<style scoped>
ul {
  list-style-type: none;
  padding-inline-start: 0;
  margin: 0;
}

li {
  cursor: pointer;
  padding: 12px;
}

li:hover {
  background-color: rgb(241, 241, 241);
}
.separator {
  border-top: 1px solid silver;
  padding: 0;
}

.menuitem-icon {
  margin-right: 12px;
}
.play {
  color: #689f38;
}

.remove {
  color: #d32f2f;
}

.checkbox-container {
  padding: 0.5rem;
}

.checkbox-list {
  max-height: 10rem;
  overflow-y: auto;
  margin-top: 0.5rem;
  padding-right: 0.5rem;
}

.checkbox-list::-webkit-scrollbar {
  width: 6px;
}

.checkbox-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.checkbox-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.checkbox-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
